import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  cardDetailSchema,
  cardMirrorCreateBodySchema,
  cardMirrorSchema,
  type CardDetail,
  type CardMirror,
  type CardMirrorCreateBody,
} from '@molt/shared';
import { announce } from './announce';
import { del, emptyResponseSchema, get, post } from './api';
import { queryKeys } from './query-keys';

type CardMirrorTranslate = (
  key: string,
  values?: Record<string, string | number>,
  count?: number,
) => string;

export interface CreateCardMirrorVariables {
  readonly cardId: string;
  readonly title: string;
  readonly sourceBoardTitle: string;
  readonly listId: string;
  readonly destinationBoardId: string;
  readonly destinationBoardTitle: string;
  readonly destinationListTitle: string;
  readonly previousSurfaceId: string | null;
  readonly nextSurfaceId: string | null;
}

export interface DeleteCardMirrorVariables {
  readonly mirrorId: string;
  readonly title: string;
}

export interface ConvertLinkCardVariables {
  readonly cardId: string;
  readonly title: string;
}

export function createCardMirror(
  cardId: string,
  body: CardMirrorCreateBody,
): Promise<CardMirror> {
  return post(
    `/cards/${encodeURIComponent(cardId)}/mirrors`,
    cardMirrorSchema,
    cardMirrorCreateBodySchema.parse(body),
  );
}

export function getCardMirror(mirrorId: string): Promise<CardMirror> {
  return get(`/card-mirrors/${encodeURIComponent(mirrorId)}`, cardMirrorSchema);
}

export function deleteCardMirror(mirrorId: string): Promise<null> {
  return del(`/card-mirrors/${encodeURIComponent(mirrorId)}`, emptyResponseSchema);
}

export function convertLinkCard(cardId: string): Promise<CardDetail> {
  return post(`/cards/${encodeURIComponent(cardId)}/convert-link-card`, cardDetailSchema);
}

export function useCreateCardMirror(
  boardId: string,
  t: CardMirrorTranslate,
): UseMutationResult<CardMirror, unknown, CreateCardMirrorVariables> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) =>
      createCardMirror(variables.cardId, {
        listId: variables.listId,
        previousSurfaceId: variables.previousSurfaceId,
        nextSurfaceId: variables.nextSurfaceId,
      }),
    onSuccess: (mirror, variables) => {
      queryClient.setQueryData(queryKeys.cards.mirror(mirror.id), mirror);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(variables.destinationBoardId),
      });
      announce(
        t('primitives.cardMirrors.createSucceeded', {
          title: variables.title,
          list: variables.destinationListTitle,
          board: variables.destinationBoardTitle,
        }),
      );
    },
    onError: (_error, variables) => {
      announce(t('primitives.cardMirrors.createFailed', { title: variables.title }));
    },
  });
}

export function useDeleteCardMirror(
  boardId: string,
  t: CardMirrorTranslate,
): UseMutationResult<null, unknown, DeleteCardMirrorVariables> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => deleteCardMirror(variables.mirrorId),
    onSuccess: (_deleted, variables) => {
      queryClient.removeQueries({ queryKey: queryKeys.cards.mirror(variables.mirrorId) });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(boardId),
      });
      announce(t('primitives.cardMirrors.removeSucceeded', { title: variables.title }));
    },
    onError: (_error, variables) => {
      announce(t('primitives.cardMirrors.removeFailed', { title: variables.title }));
    },
  });
}

/**
 * `POST /cards/:id/convert-link-card` — `FR-TMPL-027` AC3.
 *
 * ⚠⚠ **THE ANNOUNCEMENT NAMES THE TITLE THE SERVER WROTE, NEVER THE ONE THE
 * CALLER PASSED**, and that is a disclosure decision rather than a tidiness
 * one. The route now resolves the converted card's title for *this* caller
 * under the card read scope — the target's real title when they may read it,
 * and the bare **hostname** when they may not, or when no such card exists,
 * one value for both (`apps/server/src/routes/card-link-cards.ts`,
 * `resolveConversion`). `variables.title` is the *pre-conversion* title, and
 * on both surfaces that call this hook it is a value the response has
 * deliberately replaced:
 *
 * - from the **board face**, `BoardFaceLinkCardSurface.title` is redacted to a
 *   neutral placeholder whenever `linkTargetUnavailable`, so announcing it
 *   would name the card by a title it has never had;
 * - from the **card back**, `CardDetail.title` for an internal paste is still
 *   the **raw pasted URL**, whose path carries `slugify(<the target's title>)`
 *   by `cardPath`'s construction. Announcing that speaks the target's title
 *   into the shell's live region for a viewer who may not open that card —
 *   precisely what `FR-TMPL-027`'s edge case forbids. The response's title is
 *   the hostname alone, so it cannot.
 *
 * ⚠ This does **not** close that leak, and must not be read as closing it: the
 * card back still renders the stored title on screen, and the fix belongs at
 * creation, in `apps/server/src/routes/cards.ts`'s
 * `insertLinkCardInTransaction`. It closes the one instance this file owns.
 *
 * `onError` keeps `variables.title` on purpose — there is no response to read
 * a title from, and the card is unchanged, so the pre-conversion title is the
 * *correct* name for it there.
 *
 * There is no empty-title fallback because `detail.title` cannot be empty:
 * `cardDetailSchema` extends `cardSchema`, whose `title` is `cardTitleSchema`
 * (`z.string().trim().min(1)`), and `post()` parses the response before
 * `onSuccess` runs. A guard there would be a branch no test could reach.
 */
export function useConvertLinkCard(
  boardId: string,
  t: CardMirrorTranslate,
): UseMutationResult<CardDetail, unknown, ConvertLinkCardVariables> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => convertLinkCard(variables.cardId),
    // `variables` is deliberately not bound here: the announcement below reads
    // the response, and a bound-but-unused second parameter is how the
    // pre-conversion title creeps back in.
    onSuccess: (detail) => {
      queryClient.setQueryData(queryKeys.cards.detail(detail.id), detail);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(boardId),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.cards.all() });
      announce(t('primitives.cardMirrors.convertSucceeded', { title: detail.title }));
    },
    onError: (_error, variables) => {
      announce(t('primitives.cardMirrors.convertFailed', { title: variables.title }));
    },
  });
}
